import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { PanelModule } from 'primeng/panel';
import { TableModule, TableRowCollapseEvent, TableRowExpandEvent } from 'primeng/table';
import { ToastModule } from 'primeng/toast';

export interface DocumentReference {
  documentId?: `${string} - ${string}`
  fileName?: `${string} - ${string}`
  documentListId?: string
  description?: string
}

export interface TableData {
  id: number;
  formattedId: string;
  recordSubject: string;
  providerName: string;
  doctor: string;
  address: string;
  email: string;
  rating?: number;
  recordRequestType: string;
  dateOfServices: string;
  additionalInfo: {
    isCollapsed: boolean;
    details?: RecordDetails;
  };
  documents?: DocumentReference[];
  [key: string]: any;
}

export interface RecordDetails {
  imaging?: {
    type: 'billing' | string;
    bodyPart: string;
    comments?: string;
  };
  ambulance?: {
    dateOfService: string;
    timeOfService: string;
    pickupAddress: string;
    dropAddress: string;
  };
  employment?: {
    ssn: string;
  };
  insurance?: {
    claimNumber: string;
    policyNumber: string;
  };
  irs?: {
    ssn: string;
  };
  military?: {
    branch?: string;
    serviceNumber?: string;
  };
  socialSecurity?: {
    ssn: string;
    address: string;
  };
  union?: {
    membershipNumber?: string;
  };
  va?: {
    details: string;
  };
  other?: {
    recordDescription: string;
  };
}


@Component({
  selector: 'app-record-index-table',
  imports: [TableModule, ToastModule, ButtonModule, CommonModule, AccordionModule, PanelModule, DropdownModule, FormsModule],
  providers: [MessageService],
  templateUrl: './record-index-table.component.html',
  styleUrl: './record-index-table.component.scss'
})
export class RecordIndexTableComponent implements OnChanges {
  @Input() tableData: TableData[] = []
  @Input() isTableLoading: boolean = false;
  @Input() isDocumentsLoading: boolean = false;

  @Output() tableAction = new EventEmitter();
  
  totalRecords: number = this.tableData?.length || 0;

  expandedRows = {};

  sortField: string = 'formattedId';
  sortOrder: number = 1;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tableData']) {
      this.totalRecords = this.tableData?.length || 0;
    }
  }

  expandAll() {
    this.expandedRows = this.tableData?.reduce((acc: { [x: string]: boolean; }, p: { id: string | number; }) => (acc[p.id] = true) && acc, {}) || {};
  }

  collapseAll() {
    this.expandedRows = {};
  }

  onPage(page: any) {
    this.tableAction.emit({ action: 'pageChange', data: page });
  }

  getSeverity(status: string) {
    return 'success';
  }

  getStatusSeverity(status: string) {
    return 'warn';
  }

  onRowExpand(event: TableRowExpandEvent) {

  }

  onRowCollapse(event: TableRowCollapseEvent) {

  }

  

  togglePanel(additionalData: any, event: Event) {
    additionalData.isCollapsed = !additionalData.isCollapsed;
    event.stopPropagation(); // Prevents unwanted behavior
  }

  hasDocs(): boolean {
    return this.tableData?.some(request => (request.documents?.length ?? 0) > 0) || false;
  }

  downloadFile(file: DocumentReference) {
    this.tableAction.emit({ action: 'downloadSingleFile', data: file });
  }

  downloadMultipleFiles(files: TableData) {
    this.tableAction.emit({ action: 'downloadMultipleFiles', data: files?.documents?.[files.id - 1] });
  }

  downloadAllFiles() {
    this.tableAction.emit({ action: 'downloadAllFiles', data: this.tableData.map((data) => data.documents) });
  }

  onSort(event: any) {
    this.sortField = event.field;
    this.sortOrder = event.order;

    this.tableData = [...this.tableData].sort((a, b) => {
      const valueA = this.getSortValue(a[this.sortField], this.sortField);
      const valueB = this.getSortValue(b[this.sortField], this.sortField);
      
      if (this.sortField === 'formattedId') {
        return this.sortOrder * (Number(valueA) - Number(valueB));
      }
      
      return this.sortOrder * (valueA < valueB ? -1 : valueA > valueB ? 1 : 0);
    });
  }

  private getSortValue(value: any, field: string): string | number {
    if (!value) return -Infinity;
    
    if (field === 'formattedId') {
      const [prefix, suffix] = value.split('-');
      return parseInt(suffix, 10) || -Infinity;
    }
    
    if (!isNaN(Number(value))) {
      return Number(value);
    }
    
    if (typeof value === 'string') {
      return value.toLowerCase();
    }
    
    return value.toString().toLowerCase();
  }
}
